import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";

interface ContestForm {
    contest_name: string;
    contest_date: string;
    contest_end_date: string;
    result_date: string;
    reward: string;
    reward_worth: number;
    entry_fee: string;
    total_slots: number;
}

const EditContest: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const [form, setForm] = useState<ContestForm>({
        contest_name: "",
        contest_date: "",
        contest_end_date: "",
        result_date: "",
        reward: "",
        reward_worth: 0,
        entry_fee: "",
        total_slots: 0,
    });

    const formatForDatetimeLocal = (isoString: string) => {
        const date = new Date(isoString);
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - offset * 60 * 1000);
        return localDate.toISOString().slice(0, 16);
    };

    useEffect(() => {
        if (location.state?.contest) {
            const c = location.state.contest;
            setForm({
                contest_name: c.contest_name,
                contest_date: formatForDatetimeLocal(c.contest_date),
                contest_end_date: formatForDatetimeLocal(c.contest_end_date),
                result_date: formatForDatetimeLocal(c.result_date),
                reward: c.reward,
                reward_worth: c.reward_worth,
                entry_fee: c.entry_fee,
                total_slots: c.total_slots,
            });
        } else {
            fetchContest();
        }
    }, []);

    const fetchContest = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/contest/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
                },
            });
            const data = await res.json();
            if (data.success && data.contest) {
                const c = data.contest;
                setForm({
                    contest_name: c.contest_name,
                    contest_date: formatForDatetimeLocal(c.contest_date),
                    contest_end_date: formatForDatetimeLocal(c.contest_end_date),
                    result_date: formatForDatetimeLocal(c.result_date),
                    reward: c.reward,
                    reward_worth: c.reward_worth,
                    entry_fee: c.entry_fee,
                    total_slots: c.total_slots,
                });
            } else {
                Swal.fire("Error", "Failed to load contest details", "error");
                navigate("/admin/manage-contests");
            }
        } catch (err) {
            Swal.fire("Error", "Server error while loading contest", "error");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: name === "reward_worth" || name === "total_slots" ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const res = await fetch(`${API_BASE_URL}/admin/contest/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
                },
                body: JSON.stringify(form),
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire({
                    title: "Updated!",
                    text: data.message,
                    icon: "success",
                    confirmButtonText: "OK",
                    customClass: {
                        confirmButton: "bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700",
                    },
                    buttonsStyling: false,
                });
                navigate("/admin/manage-contests");
            } else {
                Swal.fire("Error", data.error, "error");
            }
        } catch (err) {
            Swal.fire("Error", "Server error while updating contest", "error");
        }
    };

    return (
        <div className="pt-24 max-w-2xl mx-auto px-4 pb-10">
            <h2 className="text-2xl font-bold mb-6 text-indigo-700">Edit Contest</h2>
            <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow p-6 rounded-lg">
                {[
                    { name: "contest_name", label: "Contest Name", type: "text" },
                    { name: "contest_date", label: "Contest Date", type: "datetime-local" },
                    { name: "contest_end_date", label: "Contest End Date", type: "datetime-local" },
                    { name: "result_date", label: "Result Date", type: "datetime-local" },
                    { name: "reward", label: "Reward", type: "text" },
                    { name: "reward_worth", label: "Reward Worth", type: "number" },
                    { name: "entry_fee", label: "Entry Fee", type: "text" },
                    { name: "total_slots", label: "Total Slots", type: "number" },
                ].map(({ name, label, type }) => (
                    <div key={name} className="relative">
                        <input
                            id={name}
                            type={type}
                            name={name}
                            value={form[name as keyof ContestForm]}
                            onChange={handleChange}
                            required
                            className="peer w-full border border-gray-300 px-3 pt-5 pb-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder=" "
                        />
                        <label
                            htmlFor={name}
                            className="absolute left-3 top-2 text-sm text-gray-500 transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-indigo-600"
                        >
                            {label}
                        </label>
                    </div>
                ))}

                <button
                    type="submit"
                    className="bg-indigo-600 w-full text-white px-6 py-2 rounded hover:bg-indigo-700 transition"
                >
                    Update Contest
                </button>
            </form>
        </div>
    );
};

export default EditContest;
